'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const antalyaProduct: Product = {
  slug: 'antalya-bondex-sunger',
  name: 'Antalya Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Antalya bondex sünger; konut, otel, villa, ofis ve teknik hacim projelerinde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Antalya bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Antalya içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-8.webp',
  sku: 'BDX-ANT',
  mpn: 'BDX-ANT',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Antalya bondex sünger nerelerde kullanılır?',
      answer:
        'Konut, villa, apart otel, ticari ofis, teknik oda, jeneratör hacmi ve mekanik tesisat alanlarında kullanılır. Antalya’da özellikle otel ve konut projelerinde sık tercih edilir.',
    },
    {
      question: 'Antalya içinde teslimat süresi ne kadar?',
      answer:
        'Stok durumuna bağlı olarak Antalya merkez ve çevre ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük metrajlı işlerde etaplı sevkiyat uygulanır.',
    },
    {
      question: 'Hangi kalınlık tercih edilmeli?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek ihtiyaçlarda 30-40mm, zorlayıcı teknik senaryolarda 50mm veya bariyerli sistemler önerilir.',
    },
    {
      question: "Antalya'da keşif ve teknik destek veriyor musunuz?",
      answer:
        'Evet. Proje tipine göre metraj doğrulama, katman önerisi ve uygulama planlaması için teknik yönlendirme sağlanır.',
    },
    {
      question: 'Antalya bondex sünger fiyatı ne kadar?',
      answer:
        "Antalya bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat, kalınlık, metraj ve uygulama senaryosuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Barış Yılmaz',
      datePublished: '2026-04-02',
      ratingValue: 5,
      title: 'Lara otel projesinde memnun kaldık',
      body:
        "Lara bölgesindeki otel teknik alanlarında kullandık. Mekanik hacimden gelen gürültü belirgin şekilde düştü.",
      verified: true,
    },
    {
      authorName: 'Merve Demir',
      datePublished: '2026-04-08',
      ratingValue: 5,
      title: 'Kepez konut yenilemesinde fark yarattı',
      body:
        "Daire yenilemesinde zemin altında uyguladık. Komşu kaynaklı darbe sesi eskiye göre çok daha az hissediliyor.",
      verified: true,
    },
    {
      authorName: 'Onur Kaya',
      datePublished: '2026-04-13',
      ratingValue: 4,
      title: 'Muratpaşa ofisinde iyi denge',
      body:
        'Toplantı odası geçişlerinde ses taşmasını düşürmek için uyguladık. Beklentimizi karşılayan bir sonuç aldık.',
      verified: true,
    },
  ],
};

export function CityBondexAntalya() {
  const url = toAbsoluteUrl('/antalya-bondex-sunger');
  const title = 'Antalya Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Antalya bondex sünger, konut ve turizm projelerinde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl(
    'Merhaba, Antalya bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (antalya-bondex-sunger)'
  );

  const displayPrice = antalyaProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(antalyaProduct.priceTRY)
    : null;

  const pageReviews =
    antalyaProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Antalya içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Antalya merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik yönlendirme' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Antalya Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(antalyaProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Antalya Bondex Sünger' },
          { '@type': 'Thing', name: 'ses yalıtım süngeri' },
          { '@type': 'Thing', name: 'Antalya ses yalıtımı' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: antalyaProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(antalyaProduct.imagePath), width: 1200, height: 750 }],
        description: antalyaProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: antalyaProduct.category,
        url,
        sku: antalyaProduct.sku,
        mpn: antalyaProduct.mpn,
        productID: antalyaProduct.sku || antalyaProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: antalyaProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Antalya' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(antalyaProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(antalyaProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: antalyaProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: antalyaProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Antalya Bondex Sünger Nedir? Nerede ve Nasıl Kullanılır?</h2>
            <p className={styles.seoText}>
              Antalya'da ses yalıtımı planı yapılırken Türkiye'nin birçok şehrinden farklı iki temel gerçek aynı anda değerlendirilmelidir:
              birincisi turizm odaklı yapı stoğu, ikincisi hızlı büyüyen konut ve ticari dönüşüm. Lara, Konyaaltı, Muratpaşa, Kepez, Döşemealtı,
              Alanya, Manavgat ve Kemer gibi bölgelerde farklı yapı tipleri yan yana bulunduğu için tek bir reçete ile tüm projelere aynı çözümü
              vermek doğru değildir. Antalya bondex sünger yaklaşımı bu nedenle "ürün adı odaklı" değil "senaryo odaklı" kurgulanır.
            </p>
            <p className={styles.seoText}>
              Bondex sünger, rebonded süngerpan yapısı sayesinde darbe ve titreşim kaynaklı enerjinin yüzeyler üzerinden yayılımını azaltmaya
              yardımcı olan güçlü bir katman çözümüdür. Ancak sahada iyi sonuç almak için sadece malzeme seçimi yetmez; hangi yüzeyde hangi
              kalınlığın kullanılacağı, kenar dönüşlerinin nasıl yapılacağı, birleşim noktalarının nasıl kapatılacağı ve uygulama sırasının
              nasıl yönetileceği en az ürün kadar önemlidir. Antalya'da özellikle otel, apart ve rezidans projelerinde aynı bina içinde birden
              fazla kullanım tipi bulunduğundan, proje detayına göre katman dağılımı doğru kurgulanmalıdır.
            </p>
            <p className={styles.seoText}>
              Antalya'nın iklim koşulları da uygulama disiplinini etkiler. Yılın büyük kısmında sıcaklık ve nem değerleri yüksek seyrettiği
              için yüzey hazırlığı, yapıştırıcı seçimi ve uygulama zamanlaması doğru planlanmazsa performans düşebilir. Bu nedenle keşif aşaması
              sadece metraj çıkarmak için değil, yüzey davranışını ve uygulama riskini görmek için de kritik bir adımdır.
            </p>
            <h3 className={styles.seoSubtitle}>Antalya'da En Çok Karşılaşılan Ses Problemleri</h3>
            <p className={styles.seoText}>
              Konut tarafında kullanıcıların en sık ifade ettiği problem katlar arası darbe sesidir. Özellikle gece saatlerinde üst komşudan
              gelen adım ve sürükleme sesi yaşam konforunu bozar. İkinci sırada duvar kaynaklı konuşma geçişleri yer alır. Ticari yapılarda ve
              ofislerde ise toplantı odası mahremiyeti, açık ofis uğultusu ve teknik odadan gelen sürekli gürültü öne çıkar. Turizm tesislerinde
              mekanik hacimlerin konaklama alanlarına yakın olması nedeniyle titreşim kaynaklı şikayetler daha sık görülür.
            </p>
            <p className={styles.seoText}>
              Bu problemlerin her biri farklı çözüm yaklaşımı gerektirir. Darbe sesinde zemin katmanı öncelik kazanırken, konuşma geçişinde
              duvar ve birleşim detayları daha önemli hale gelir. Teknik hacimlerde ise kaynak noktaya yakın uygulama ve taşıyıcıya geçişi
              kıran sistemler gerekir. Dolayısıyla Antalya için doğru çözüm, "tek kalınlık herkese uyar" yaklaşımı değil; problem tipine göre
              katman planlamasıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Turizm Yapılarında Neden Farklı Bir Plan Gerekir?</h3>
            <p className={styles.seoText}>
              Antalya'nın otel ve apart otel projelerinde ses yalıtımı sadece teknik bir detay değil, doğrudan müşteri memnuniyeti parametresidir.
              Oda içi konfor, koridor sessizliği, teknik hacim etkisinin sınırlandırılması ve komşu birimlere ses taşmasının azaltılması,
              işletmenin yorum skorlarını bile etkileyebilir. Bu yüzden çözüm yalnızca malzeme tedariki değil; işletme akışını bozmadan etaplı
              uygulama, doğru termin planı ve bakım erişimini koruyan bir kurgu ile ele alınmalıdır.
            </p>
            <p className={styles.seoText}>
              Özellikle sezon içinde çalışan tesislerde uygulama yapılacaksa zamanlama kritik hale gelir. Yoğunluk saatleri, oda doluluk oranı,
              servis alanı erişimi ve teknik ekip koordinasyonu birlikte planlandığında hem operasyon devam eder hem de uygulama kalitesi
              korunur. Antalya'da bu tip projelerde hızlı ama plansız iş yapmak yerine, etaplı ve kontrollü yaklaşım çok daha başarılı sonuç verir.
            </p>
            <h3 className={styles.seoSubtitle}>Konut ve Villa Projelerinde Doğru Yaklaşım</h3>
            <p className={styles.seoText}>
              Antalya'da villa ve müstakil yapı sayısı yüksek olduğu için sadece apartman senaryosu üzerinden çözüm üretmek yeterli değildir.
              Villa projelerinde teknik oda, havuz ekipman alanı, mekanik ünite ve yaşam alanı arasındaki geçişler doğru yönetilmezse ses
              konforu düşebilir. Bu alanlarda bondex katmanının doğru konumlandırılması, kullanıcı tarafında belirgin fark yaratır.
            </p>
            <p className={styles.seoText}>
              Apartman ve rezidans projelerinde ise hedef çoğunlukla "tam sessizlik" değil "rahatsızlığı belirgin düşüren yaşam standardı"dır.
              Bu gerçekçi hedef, maliyet-performans dengesini korur. Oda bazlı etaplama yapıldığında kullanıcı yaşamı tamamen durmadan uygulama
              ilerler. Yatak odası, çalışma odası ve çocuk odası gibi hassas hacimlere öncelik verildiğinde daha az metrajla daha yüksek
              memnuniyet sağlanabilir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi Nasıl Yapılır?</h3>
            <p className={styles.seoText}>
              Kalınlık seçiminde ilk soru "hangi ürün daha kalın?" değil, "hangi problem daha baskın?" olmalıdır. Hafif şikayetli alanlarda
              10-20mm aralığı yeterli olabilir. Orta-yüksek şikayetlerde 30-40mm çözümler daha güçlü sonuç verir. Daha zorlayıcı teknik
              senaryolarda 50mm ve bariyerli sistemler değerlendirilir. Ancak kalınlık tek başına başarı kriteri değildir; uygulama kalitesi
              düşükse performans da düşer.
            </p>
            <p className={styles.seoText}>
              Ürün karşılaştırmasını yaparken proje tipine göre karar vermek gerekir. Genel aile için{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link>, yüksek hedefler için{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> ve gerektiğinde ileri seviye kombinasyonlar birlikte
              değerlendirilmelidir. Kararı metraj + hedef + yüzey tipi + kullanım yoğunluğu birlikte belirler.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Kalitesini Belirleyen Kritik Noktalar</h3>
            <p className={styles.seoText}>
              Sahada en sık yapılan hatalar; yüzey hazırlığını atlamak, birleşim detaylarını açık bırakmak, kenar sürekliliğini korumamak ve
              proje sırasını yanlış kurgulamaktır. Antalya gibi yoğun çalışma temposuna sahip bölgelerde bu hatalar "hızlı bitirme" baskısıyla
              daha sık görülür. Oysa doğru iş programı ile hem hız hem kalite birlikte korunabilir.
            </p>
            <p className={styles.seoText}>
              Uygulama sürecinde ara kontrol yapmak çok önemlidir. Katman yerleşimi sonrası birleşim kontrolleri, lokal düzeltmeler ve son
              kontrol adımı tamamlanmadan iş teslim edilirse proje performansı risk altına girer. Profesyonel yaklaşım, işi tek hamlede bitirmek
              değil, kontrollü adımlarla kalıcı sonuç üretmektir.
            </p>
            <h3 className={styles.seoSubtitle}>Teslimat ve Termin Planlaması</h3>
            <p className={styles.seoText}>
              Antalya şehir içi teslimat planı yapılırken mesafe kadar erişim koşulları da dikkate alınmalıdır. Merkezde trafik, turistik
              bölgelerde yoğunluk, otel projelerinde güvenlik/servis kuralları ve bina yönetimi izinleri teslimat saatini etkiler. Bu nedenle
              büyük projelerde etaplı sevkiyat tercih edilir. Etaplı plan, sahadaki stok karmaşasını azaltır ve hasar riskini düşürür.
            </p>
            <p className={styles.seoText}>
              Teknik destek süreci ise üç aşamada yürütülmelidir: uygulama öncesi ihtiyaç analizi, uygulama sırası kalite kontrol, uygulama
              sonrası değerlendirme. Bu akış hem proje sahibinin hem uygulama ekibinin aynı hedefte kalmasını sağlar. Sonuç olarak yatırımın
              karşılığı daha ölçülebilir hale gelir.
            </p>
            <h3 className={styles.seoSubtitle}>Neden Antalya'ya Özel İçerik Önemli?</h3>
            <p className={styles.seoText}>
              Antalya için hazırlanan içerik; turizm sezonu etkisi, otel+konut dengesi, ilçe bazlı kullanım farklılığı, teknik hacim yoğunluğu
              ve teslimat gerçekleri dikkate alınarak oluşturulmuştur. Amaç şehir adını değiştirip aynı metni tekrar etmek değil, Antalya'da
              gerçekten karşılaşılan senaryolara göre yönlendirme yapmaktır. Bu yaklaşım hem kullanıcı tarafında doğru beklenti oluşturur hem de
              arama tarafında özgünlük sağlar.
            </p>
            <p className={styles.seoText}>
              Projeniz için hızlı ve net öneri almak istiyorsanız yapı tipi, metraj, kullanım amacı ve temel şikayet tanımını paylaşmanız
              yeterlidir. Bu verilerle birlikte en uygun kalınlık, katman yaklaşımı ve teslimat planı daha güvenli biçimde netleştirilebilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Antalya Projelerinde Teknik Seçim Kriterleri</h2>
            <h3 className={styles.seoSubtitle}>Malzeme Yapısı</h3>
            <p className={styles.seoText}>
              Antalya bondex sünger, yüksek yoğunluklu rebonded süngerpan yapısı sayesinde darbe ve titreşim kaynaklı enerji transferini
              düşürmeye yardımcı olur. Yüzey tipi, uygulama yöntemi ve birleşim detayları doğru planlandığında daha stabil performans verir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Kararı</h3>
            <p className={styles.seoText}>
              Düşük-orta şikayetli konut/ofis senaryolarında 20-30mm dengeli bir tercih olurken, teknik hacimlerde 40mm ve üzeri seçenekler
              daha uygun olabilir. Nihai kalınlık, proje hedefi ve yüzey koşulu birlikte değerlendirilerek belirlenmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Notu</h3>
            <p className={styles.seoText}>
              Antalya ikliminde yüzey hazırlığı ve yapıştırıcı uyumu kritik olduğu için uygulama öncesi kontrol atlanmamalıdır. Ek yerlerinin
              sürekliliği korunmadığında performans kaybı oluşabilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Antalya'da Öne Çıkan Kullanım Alanları</h2>
            <h3 className={styles.seoSubtitle}>Konut ve Rezidans</h3>
            <p className={styles.seoText}>
              Katlar arası darbe sesi, komşu duvar konuşma geçişi ve ortak alan gürültüsü için zemin ve duvar detaylarında kullanılır.
            </p>
            <h3 className={styles.seoSubtitle}>Otel ve Apart Tesisler</h3>
            <p className={styles.seoText}>
              Oda konforunu etkileyen mekanik hacim ve koridor kaynaklı seslerin kontrolünde, sezon planına uygun etaplı uygulamalarla tercih edilir.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Teknik Oda</h3>
            <p className={styles.seoText}>
              Toplantı odası mahremiyeti, açık ofis uğultusu ve ekipman titreşimi yönetimi için katmanlı sistemlerde kullanılır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Proje Planlama Bilgisi</h2>
            <h3 className={styles.seoSubtitle}>Antalya İçi Sevkiyat</h3>
            <p className={styles.seoText}>
              Merkez ve çevre ilçelerde sevkiyat, şantiye erişimi ve çalışma saatine göre planlanır. Büyük metrajlarda etaplı teslimat uygulanır.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Destek</h3>
            <p className={styles.seoText}>
              Keşif, metraj doğrulama, katman önerisi ve uygulama sonrası kontrol adımları birlikte yürütülerek proje tutarlılığı artırılır.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Antalya Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={antalyaProduct.imagePath} alt="Antalya bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Antalya Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-ANT | Antalya İçi Projeler
          </p>
          <p className={styles.meta}>
            Antalya'da <strong>bondex sünger</strong> ihtiyacı olan konut, ofis, otel ve teknik hacim projelerinde doğru kalınlık seçimi,
            hızlı tedarik ve proje bazlı uygulama planı ile ilerliyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Antalya içi hızlı teslimat</span>
            <span className="chip">Otel ve konut projelerine uygun</span>
          </div>

          <div className={styles.specGrid} aria-label="Antalya için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {antalyaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Antalya Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={antalyaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
